import './TypeaheadField.css';
import { useEffect, useMemo, useRef, useState } from 'react';

let typeaheadFieldIdCounter = 0;

function getNextTypeaheadFieldId() {
  typeaheadFieldIdCounter += 1;
  return `typeahead-field-${typeaheadFieldIdCounter}`;
}

/**
 * @template TOption
 * @param {object} props
 * @param {string} props.label - Accessible label for the field.
 * @param {TOption|null} props.selectedOption - Currently selected option.
 * @param {(option: TOption|null) => void} props.onSelect - Selection handler.
 * @param {TOption[]} props.options - Available option list.
 * @param {(option: TOption) => string} props.getOptionLabel - Retrieves display label.
 * @param {(option: TOption) => string} props.getOptionKey - Retrieves unique key for an option.
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {boolean} [props.disabled=false] - Disables the control when true.
 * @param {string|null} [props.error=null] - Optional validation message.
 * @param {string} [props.description] - Optional helper description rendered below the input.
 * @param {boolean} [props.allowFreeInput=false] - Enables accepting arbitrary user input.
 */
function TypeaheadField({
  label,
  selectedOption,
  onSelect,
  options,
  getOptionLabel,
  getOptionKey,
  placeholder = '',
  disabled = false,
  error = null,
  description,
  allowFreeInput = false,
}) {
  const idRef = useRef(null);
  if (idRef.current === null) {
    idRef.current = getNextTypeaheadFieldId();
  }
  const inputId = idRef.current;
  const listboxId = `${inputId}-listbox`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (selectedOption) {
      setQuery(getOptionLabel(selectedOption));
    } else {
      setQuery('');
    }
  }, [getOptionLabel, selectedOption]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options.slice(0, 6);
    }
    return options
      .filter((option) =>
        getOptionLabel(option).toLowerCase().includes(normalizedQuery),
      )
      .slice(0, 6);
  }, [getOptionLabel, options, query]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    if (filteredOptions.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(0);
  }, [filteredOptions, isOpen]);

  const handleSelect = (option) => {
    onSelect(option);
    setIsOpen(false);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setIsOpen(true);
    if (!value.trim()) {
      onSelect(null);
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => {
        if (filteredOptions.length === 0) {
          return -1;
        }
        const nextIndex = prev + 1;
        if (nextIndex >= filteredOptions.length) {
          return 0;
        }
        return nextIndex;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => {
        if (filteredOptions.length === 0) {
          return -1;
        }
        if (prev <= 0) {
          return filteredOptions.length - 1;
        }
        return prev - 1;
      });
    } else if (event.key === 'Enter') {
      const normalizedQuery = query.trim();
      if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
        event.preventDefault();
        handleSelect(filteredOptions[activeIndex]);
      } else if (allowFreeInput && normalizedQuery) {
        event.preventDefault();
        const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';
        if (selectedLabel.trim().toLowerCase() !== normalizedQuery.toLowerCase()) {
          onSelect(normalizedQuery);
        }
        setIsOpen(false);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    onSelect(null);
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleBlur = (event) => {
    if (!event.relatedTarget) {
      setIsOpen(false);
    } else if (listRef.current && listRef.current.contains(event.relatedTarget)) {
      return;
    }
    setIsOpen(false);

    if (!allowFreeInput || disabled) {
      return;
    }

    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      return;
    }

    const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';
    if (selectedLabel.trim().toLowerCase() === normalizedQuery.toLowerCase()) {
      return;
    }

    onSelect(normalizedQuery);
  };

  return (
    <div className="TypeaheadField">
      <label className="TypeaheadField-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="TypeaheadField-inputWrapper">
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
          }
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
          className={`TypeaheadField-input${error ? ' TypeaheadField-input--error' : ''}`}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {selectedOption ? (
          <button
            type="button"
            className="TypeaheadField-clearButton"
            onClick={handleClear}
            aria-label={`Clear ${label}`}
            disabled={disabled}
          >
            ×
          </button>
        ) : null}
      </div>
      {description ? (
        <p className="TypeaheadField-description" id={descriptionId}>
          {description}
        </p>
      ) : null}
      {error ? (
        <p className="TypeaheadField-error" role="alert" id={errorId}>
          {error}
        </p>
      ) : null}
      {isOpen && filteredOptions.length > 0 ? (
        <ul
          className="TypeaheadField-listbox"
          role="listbox"
          id={listboxId}
          ref={listRef}
        >
          {filteredOptions.map((option, index) => {
            const key = getOptionKey(option);
            const labelText = getOptionLabel(option);
            const isSelected =
              selectedOption && getOptionKey(selectedOption) === key;
            const isActive = index === activeIndex;
            return (
              <li
                id={`${listboxId}-option-${index}`}
                key={key}
                role="option"
                aria-selected={isSelected}
                className={`TypeaheadField-option${
                  isActive ? ' TypeaheadField-option--active' : ''
                }${isSelected ? ' TypeaheadField-option--selected' : ''}`}
              >
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(option)}
                  disabled={disabled}
                >
                  <span className="TypeaheadField-optionLabel">{labelText}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default TypeaheadField;
